import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { generateBarcodeDataUrl } from '@/utils/barcodeGenerator';

export interface HtmlTemplate {
  id: string;
  name: string;
  is_default: boolean;
  dimensions: {
    width: number;
    height: number;
    unit: string;
  };
  html_content: string;
  variables: string[];
}

export interface TemplateProduct {
  barcode?: string;
  product_name: string;
  retail_price?: number;
  master_categories?: {
    category_name: string;
  };
}

export const useHtmlTemplateEngine = () => {
  const [templates, setTemplates] = useState<HtmlTemplate[]>([]);
  const [defaultTemplate, setDefaultTemplate] = useState<HtmlTemplate | null>(null);
  const [loading, setLoading] = useState(true);

  const loadTemplates = useCallback(async () => {
    try {
      // For now, create a default HTML template in-memory
      // Later this can be moved to database storage
      const fallbackTemplate: HtmlTemplate = {
        id: 'default-html',
        name: 'Simple Label',
        is_default: true,
        dimensions: { width: 2.44, height: 1.14, unit: 'in' },
        html_content: `
          <div class="label">
            <div class="product-name">{{product_name}}</div>
            <div class="price">$` + `{{price}}</div>
            <div class="barcode-container">
              {{barcode_image}}
            </div>
          </div>
        `,
        variables: ['product_name', 'price', 'barcode_image']
      };

      setTemplates([fallbackTemplate]);
      setDefaultTemplate(fallbackTemplate);
    } catch (error) {
      console.error('Error loading HTML templates:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const generateBarcodeImage = useCallback((barcode: string): string => {
    if (!barcode) return '';
    
    try {
      const barcodeDataUrl = generateBarcodeDataUrl(barcode, {
        width: 2,
        height: 40,
        displayValue: false,
        margin: 0,
        fontSize: 12
      });
      
      return barcodeDataUrl ? `<img src="${barcodeDataUrl}" alt="Barcode" class="barcode" />` : '';
    } catch (error) {
      console.error('Error generating barcode:', error);
      return '';
    }
  }, []);

  const generateHtmlLabel = useCallback((template: HtmlTemplate, product: TemplateProduct): string => {
    let htmlContent = template.html_content;
    
    // Replace template variables with product data
    const price = product.retail_price ? product.retail_price.toFixed(2) : '0.00';
    const replacements: Record<string, string> = {
      '{{product_name}}': product.product_name || 'Product Name',
      '{{price}}': price,
      '{{barcode_image}}': generateBarcodeImage(product.barcode || ''),
      '{{category}}': product.master_categories?.category_name || '',
      '{{barcode}}': product.barcode || ''
    };

    // Apply replacements
    Object.entries(replacements).forEach(([placeholder, value]) => {
      htmlContent = htmlContent.replace(new RegExp(placeholder, 'g'), value);
    });

    // Add CSS styling for print-ready output
    const { width, height, unit } = template.dimensions;
    const widthIn = unit === 'mm' ? width / 25.4 : width;
    const heightIn = unit === 'mm' ? height / 25.4 : height;

    return `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1">
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            
            body {
              margin: 0;
              padding: 0;
              font-family: Arial, sans-serif;
              width: ${widthIn}in;
              height: ${heightIn}in;
              overflow: hidden;
            }
            
            .label {
              width: 100%;
              height: 100%;
              display: flex;
              flex-direction: column;
              justify-content: center;
              align-items: center;
              padding: 0.1in;
              background: white;
              border: 1px solid #ddd;
            }
            
            .product-name {
              font-size: 14px;
              font-weight: bold;
              text-align: center;
              margin-bottom: 0.05in;
              line-height: 1.1;
              max-width: 100%;
              word-wrap: break-word;
              overflow: hidden;
              display: -webkit-box;
              -webkit-line-clamp: 2;
              -webkit-box-orient: vertical;
            }
            
            .price {
              font-size: 16px;
              font-weight: bold;
              color: #d63384;
              margin-bottom: 0.05in;
            }
            
            .barcode-container {
              flex: 1;
              display: flex;
              align-items: center;
              justify-content: center;
            }
            
            .barcode {
              max-width: 100%;
              max-height: 100%;
              object-fit: contain;
            }
            
            @media print {
              body {
                margin: 0;
                padding: 0;
              }
              
              .label {
                border: none;
              }
              
              * {
                -webkit-print-color-adjust: exact !important;
                color-adjust: exact !important;
              }
            }
            
            @page {
              size: ${widthIn}in ${heightIn}in;
              margin: 0;
            }
          </style>
        </head>
        <body>
          ${htmlContent}
        </body>
      </html>
    `;
  }, [generateBarcodeImage]);

  return {
    templates,
    defaultTemplate,
    loading,
    loadTemplates,
    generateHtmlLabel
  };
};